import type { LiveV1ClaimRequest, LiveV1EditingConfig, LiveV1ReferenceImageInput } from '@/app/services/live';

export type LiveCallMode = 'video';
/**
 * 编辑场景。推导源是 editing_config.scene——claim 请求上的扁平 live_scene 已随
 * 后端 2026-09-01 的 live 协议更新删除，改为嵌在 editing_config 里。
 */
export type LiveScene = Exclude<LiveV1EditingConfig['scene'], 'unspecified' | undefined>;

/**
 * 参考图入参。协议是 oneof：要么给素材库 id，要么给 uri，不能两个都给。
 * 生成出来的类型两个字段都是可选的，表达不出互斥，这里用联合类型收紧。
 */
export type LiveReferenceImageInput =
  | (Pick<LiveV1ReferenceImageInput, 'uri'> & { uri: string; editing_asset_id?: never })
  | (Pick<LiveV1ReferenceImageInput, 'editing_asset_id'> & { editing_asset_id: string; uri?: never });

export type LiveClaimRequest = Omit<LiveV1ClaimRequest, 'call_mode'> & {
  call_mode: LiveCallMode;
};

export interface LiveClaimInfo {
  id: string;
  stream_url: string;
  live_duration: number;
  status: string;
}

export interface LiveClaimRtcInfo {
  app_id: string;
  channel_id: string;
  nonce?: string;
  role?: number | string;
  session_id?: string;
  timestamp?: number | string;
  user_id: string;
  token: string;
  token_expire_at: string;
}

export interface LiveClaimResponse {
  live: LiveClaimInfo;
  is_success: boolean;
  rtc: LiveClaimRtcInfo;
}

export interface LiveClaimErrorPayload {
  code?: number;
  message?: string;
  reason?: string;
}

export type LiveClaimErrorCode =
  | 'claim_not_enough_quota'
  | 'invalid_claim_response'
  | 'session_store_failed'
  | 'unauthorized'
  | 'request_failed';

export class LiveClaimError extends Error {
  code: LiveClaimErrorCode;
  reason?: string;
  status?: number;

  constructor({
    code,
    message,
    reason,
    status,
  }: {
    code: LiveClaimErrorCode;
    message: string;
    reason?: string;
    status?: number;
  }) {
    super(message);
    this.name = 'LiveClaimError';
    this.code = code;
    this.reason = reason;
    this.status = status;
  }
}
/**
 * 直播调试链路跨路由传递的最小元数据。
 * - session_correlation_id：一次通话的前端追踪 id，claim 侧生成，直播页恢复复用
 * - avatar_type：直播页恢复 logger context 时需要，按 UGC/PGC 聚合事件
 * - claim_request_started_at_perf_ms / claim_success_at_perf_ms：单调时钟里程碑，仅同 document 有效；
 * - claim_request_started_at_perf_ms：麦克风授权完成后、claimLive 请求发起时的单调时钟里程碑；
 *   performance 不可用或跨 document 刷新时直接省略，不写占位值；
 *   直播页恢复前需 `Number.isFinite` 校验，缺失时直接省略对应耗时字段。
 * 只允许可序列化原始字段；logger 实例不在此保存。
 */
export interface LiveClaimDebugMetadata {
  session_correlation_id: string;
  avatar_type?: 'ugc' | 'pgc';
  claim_request_started_at_perf_ms?: number;
  claim_success_at_perf_ms?: number;
}

export interface StoredLiveClaimSession {
  /** 入口麦克风预检得到的设备 ID，仅在当前页面会话内传给 RTC SDK。 */
  audioCaptureDeviceId?: string;
  /** 当前页面会话已经成功完成麦克风权限和采集预检。 */
  microphonePreflightCompleted?: boolean;
  avatarId?: string;
  /** 预留头像图 URL；接口补充该字段后即可在 connecting 态直接展示。 */
  avatarUrl?: string;
  claimedAt: string;
  claim: LiveClaimResponse;
  /** 小程序 WebView 当前直播会话的传输凭证；只保存在内存 Store，不做持久化。 */
  transportAccessToken?: string;
  /** 调试链路元数据；老流程/刷新场景可为空，直播页需要 fallback。 */
  debugMetadata?: LiveClaimDebugMetadata;
}

/**
 * 通话模块使用的头像实体，对齐 Android AvatarDetailBean 的展示相关字段。
 * 仅保留 UI 展示需要的字段，后端补充头像接口后可直接映射。
 */
export type {
  LiveV1Avatar,
  LiveV1AvatarRole as LiveAvatar,
  LiveV1AvatarVoiceLibrary,
  LiveV1AvatarWithVoice,
  LiveV1CreatePersonalVoiceReply,
  LiveV1CreateVoiceCloneReply,
  LiveV1GetLivePlaybackShareDetailReply,
  LiveV1LivePlayback,
  LiveV1VoiceLibrary,
} from '@/app/services/live';

/** 通话页非主动退出的原因；返回 /live 后据此给特殊提示（对齐 iOS / Android 的挂断提示机制）。 */
export type LiveExitReason =
  | 'duration_limit'
  | 'connect_timeout'
  | 'not_ready'
  | 'connection_lost'
  | 'server_force_hangup'
  | 'timeout_hangup'
  | 'audit_rejected'
  | 'internal_fail'
  | 'ended'
  | 'realtime_model_fail'
  | 'prepared_timeout'
  | 'user_hangup'
  | 'app_close_conn'
  | 'prepared_sip_disconnected'
  | 'sip_reconnect_timeout';

/**
 * 一次退出提示。reason 决定兜底文案（live.exitNotice.{reason}）；
 * message 为服务端 force_hangup 等下发的自定义文案，非空时优先显示。
 */
export interface LiveExitNotice {
  reason: LiveExitReason;
  message?: string;
}
